import { useState } from "react";

// Copy-able presentation of a DNS record (or mailbox address). Used in
// the onboarding wizard's final step and on the settings page so the
// customer always sees the same shape when they need to publish or
// double-check a record.
//
// The `type` field is rendered as a label (TXT / Email / …) — keep it
// short and conventional so it stays one character row in the grid.

export function DnsRecordBox({
  name,
  type,
  value,
  copyLabel,
}: {
  name: string;
  type: string;
  value: string;
  // Custom label for the copy button; defaults to "Copy value" which
  // matches the original onboarding usage. Pages that copy something
  // other than the value (e.g. the full address) override it.
  copyLabel?: string;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard API blocked; user can copy manually */
    }
  };
  return (
    <div className="mt-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3 text-xs">
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono">
        <span className="text-slate-400 dark:text-slate-500">name</span>
        <span className="text-slate-700 dark:text-slate-200 break-all">{name}</span>
        <span className="text-slate-400 dark:text-slate-500">type</span>
        <span className="text-slate-700 dark:text-slate-200">{type}</span>
        <span className="text-slate-400 dark:text-slate-500">value</span>
        <span className="text-slate-700 dark:text-slate-200 break-all">{value}</span>
      </div>
      <button
        onClick={copy}
        className="mt-2 rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        {copied ? "Copied!" : copyLabel ?? "Copy value"}
      </button>
    </div>
  );
}
